import { createGlobalStyle, css } from 'styled-components';

import { stableScrollGutter } from '../../assets/styles/components/scrollGutter';
import {
  atPaneWidth,
  rootAtPaneWidth,
  WIDE_PANE_MIN_WIDTH,
} from '../../assets/styles/components/paneWidth';
import { utilityClasses } from './utilityClasses';
import { resolveThemeColorValues, ThemeName, themes } from './variables';
import { DEFAULT_THEME_COLOR, THEME_COLORS } from '../../utils/themeColor';

const THEME_NAMES = Object.keys(themes) as ThemeName[];

const toCssVariables = (values: Record<string, string>) =>
  Object.entries(values)
    .map(
      ([color, value]) => `
  --${color}-rgb: ${value};
  --${color}: rgb(${value});`,
    )
    .join('');

// The default theme colour is part of each theme, so the page is coloured before data-theme-color is set.
const colorVariables = THEME_NAMES.map(
  (theme) => `
:root[data-theme='${theme}'] {${toCssVariables({
    ...themes[theme],
    ...resolveThemeColorValues(DEFAULT_THEME_COLOR, theme),
  })}
}`,
).join('');

const themeColorVariables = THEME_COLORS.filter((color) => color !== DEFAULT_THEME_COLOR)
  .flatMap((color) =>
    THEME_NAMES.map(
      (theme) => `
:root[data-theme='${theme}'][data-theme-color='${color}'] {${toCssVariables(
        resolveThemeColorValues(color, theme),
      )}
}`,
    ),
  )
  .join('');

const GlobalStyle = createGlobalStyle`
:root {
  interpolate-size: allow-keywords;
  scroll-behavior: smooth;
  --spacing-gutter: 10px;
  --spacing-date-group: 5px;
  --scrollbar-width: 6px;
}

/* The task pane can be resized down to a narrow strip, so the outer gutter tightens with it. */
${rootAtPaneWidth(
  'min-width',
  WIDE_PANE_MIN_WIDTH,
  css`
    --spacing-gutter: 15px;
    --spacing-date-group: 10px;
  `,
)}

/* Any scroll box whose parent has inline padding to donate: the strip the scrollbar sits in is
   reserved whether or not the content overflows and taken out of that padding, so the content
   keeps the same space on both sides. See assets/styles/components/scrollGutter. */
.scroll-page-gutter,
.scroll-stable-gutter {
  ${stableScrollGutter}
}

.scroll-page-gutter {
  padding-top: var(--spacing-gutter);
}

/*
 * Custom auto-hiding overlay scrollbars.
 */
*::-webkit-scrollbar {
  width: var(--scrollbar-width);
  height: var(--scrollbar-width);
}

*::-webkit-scrollbar-track {
  background: transparent;
}

*::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

/* visible while scrolling, and while the pointer rests on the thumb (for dragging) */
.is-scrolling::-webkit-scrollbar-thumb,
*::-webkit-scrollbar-thumb:hover {
  background-color: #e0e0e0;
}

*,
*:before,
*:after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

${colorVariables}

${themeColorVariables}

html {
  height: 100%;
  scroll-behavior: smooth;
}

body {
  min-height: 100%;
  line-height: 1.5;
  font-family: "Radio Canada", sans-serif;;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-text-size-adjust: 100%;
  text-rendering: optimizeSpeed;
  text-wrap: pretty;
}

img,
picture,
video,
canvas,
svg {
  display: block;
  max-width: 100%;
  height: auto;
  font-style: italic;
}

input,
button,
textarea,
select {
  font: inherit;
}

a {
  color: inherit;
  text-decoration: none;
}

a:visited {
  color: inherit;
}

code,
kbd,
samp {
  font-family: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, 'DejaVu Sans Mono', monospace;
}

h1,
h2,
h3 {
  text-wrap: balance;
}

.visually-hidden:not(:focus):not(:active) {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

/* A dialog shown in place inside an antd popup, which already positions and paints the box. */
dialog.popup-dialog {
  position: static;
  width: auto;
  height: auto;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
}

${utilityClasses}

.settings-container{
  width: calc(var(--viewport-width) - 20px);
  margin-inline: 10px;
  inset: auto auto 12px 0px !important;
  background-color: var(--clr-container-bg);
 
 
  .settings-menu{
    padding: 10px;
    border-radius: 5px;
    background-color: var(--clr-container-bg);
    margin: 0 auto;

  }
}

/* Tab strips, their panels and the week strip clip anything outside them, so the ring goes inside.
   The doubled classes outrank antd's own tab focus rule, and its offset transition would start the
   ring outside the strip. */
.ant-tabs.ant-tabs .ant-tabs-tab.ant-tabs-tab .ant-tabs-tab-btn:focus-visible,
.ant-tabs .ant-tabs-tabpane:focus-visible,
.days-container [role='button']:focus-visible {
  outline-offset: -2px;
  transition: none;
}

.notification-container{
  width: calc(var(--viewport-width) - 20px);
  margin-inline: 10px;
  inset: 40px 0 auto auto !important;
  border-radius: 10px;
  box-shadow: 0px 0px 30px 0px var(--clr-shadow);
  overflow: hidden;
  max-width: 350px;
  background-color: var(--clr-container-bg);
  padding: 15px 15px 0;
  height: calc(var(--viewport-height) - 50px);
  /* The tab panes size off this, so the list and the empty state get the popup's height to share. */
  dialog.popup-dialog {
    height: 100%;
  }
  .notification-menu [role='button']:focus-visible {
    outline-offset: -2px;
  }
  .notification-menu{
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 15px;
    ${stableScrollGutter}
  }
  .ant-tabs-nav{
    margin-inline: 0;
    margin-top: 0;
    padding-top: 0;
  }
}

.ant-dropdown{
  &.filter-dropdown{
    background-color: var(--clr-container-bg);
    width: 294px;
    border-radius: 10px;
    box-shadow: 0px 0px 30px 0px var(--clr-shadow);
    padding: var(--spacing-gutter);
    margin-top: 12px;
    left: calc(100%  - 310px) !important;
    ${atPaneWidth(
      'min-width',
      400,
      css`
        left: calc(100% - 345px) !important;
        width: 325px !important;
      `,
    )}

    .filter-checkbox-list{
      max-height: 180px;
      overflow-y: auto;
      ${stableScrollGutter}
    }
  }
}

.ant-popover{
  &.sort-dropdown{
    width: calc(var(--viewport-width) - 20px);
    max-width: 370px;
    margin-top: 10px;
    left: 10px !important;
    ${atPaneWidth(
      'min-width',
      381,
      css`
        left: 15px !important;
      `,
    )}
    .ant-popover-inner{
      background-color: var(--clr-container-bg);
      border-radius: 10px;
      box-shadow: 0px 0px 30px 0px var(--clr-shadow);
      padding: var(--spacing-gutter);
    }
  }
}

.ant-tooltip{
  max-width: 280px;
  .ant-tooltip-inner{
    line-height: 17px;
  }
  &[class*='ant-tooltip-placement-top'],
  &[class*='ant-tooltip-placement-bottom']{
    max-width: min(304px, var(--viewport-width));
    padding-inline: 12px;
  }
}

/* antd caps these with raw viewport units, which Chromium inflates under the display size zoom. */
.ant-modal{
  max-width: calc(var(--viewport-width) - 32px);
}
@media (max-width: 575px){
  .ant-modal-root .ant-modal{
    max-width: calc(var(--viewport-width) - 16px);
  }
}
.ant-popover{
  max-width: var(--viewport-width);
}
.ant-spin-fullscreen{
  width: var(--viewport-width);
  height: var(--viewport-height);
}
.ant-dropdown-menu-vertical{
  max-height: var(--viewport-height);
}

/* status chip background colors */
.green{
  background-color: var(--tag-green);
}
.light-blue{
  background-color: var(--tag-light-blue);
}
.light-green{
  background-color: var(--tag-light-green);
}
.yellow{
  background-color: var(--tag-yellow);
}
.blue{
  background-color: var(--tag-blue);
}
.beige{
  background-color: var(--tag-beige);
}
.red{
  background-color: var(--tag-red);
}
.purple{
  background-color: var(--tag-purple);
}
.dark-grey{
  background-color: var(--tag-dark-grey);
}

/* status card border colors */
.border-green{
  border: 1px solid rgba(var(--border-green-rgb), 0.4);
}
.border-light-blue{
  border: 1px solid rgba(var(--border-light-blue-rgb), 0.4);
}
.border-light-green{
  border: 1px solid rgba(var(--border-light-green-rgb), 0.4);
}
.border-yellow{
  border: 1px solid rgba(var(--border-yellow-rgb), 0.4);
}
.border-blue{
  border: 1px solid rgba(var(--border-blue-rgb), 0.4);
}
.border-beige{
  border: 1px solid rgba(var(--border-beige-rgb), 0.4);
}
.border-red{
  border: 1px solid rgba(var(--border-red-rgb), 0.4);
}
.border-purple{
  border: 1px solid rgba(var(--border-purple-rgb), 0.4);
}
.border-dark-grey{
  border: 1px solid rgba(var(--border-dark-grey-rgb), 0.4);
}



.Toastify__toast-container{
  width: calc(var(--viewport-width) - 20px) !important;
  max-width: 500px;
  top: 12px !important;
  &:has(.login-page-toast){
    width: auto;
    max-width: 100%;
  }
  ${atPaneWidth(
    'max-width',
    480,
    css`
      &--top-center {
        left: 50% !important;
        transform: translateX(-50%) !important;
      }
    `,
  )}

}
 
 
 
:root .Toastify__toast {
  font-size: 13px;
  font-weight: 500;
  font-family: Poppins, sans-serif;
  line-height: 20px;
  min-height: 36px;
  padding: 10px;
  border-radius: 10px;
  width: 100%;
  gap: 10px;
  align-items: flex-start;
}

:root .Toastify__toast-icon {
  width: 18px;
  margin-inline-end: 0 !important;
}

/* The message is rendered as a bare text node, so it can't flex-grow to fill the toast;
   the auto margin is what keeps the close button against the trailing edge. */
:root .Toastify__close-button {
  width: 29px;
  height: 20px;
  position: unset !important;
  margin-inline-start: auto;
  display: flex;
  justify-content: end;
  flex-shrink: 0;
  opacity: 1;
}

/* react-toastify's own stylesheet sets outline: none on this button. */
:root .Toastify__close-button:focus-visible {
  outline: 2px solid var(--clr-general-primary);
  outline-offset: 1px;
  border-radius: 2px;
}

:root .Toastify__close-button .icon {
  display: flex;
}

:root .Toastify__close-button svg {
  width: 20px;
  height: 20px;
  color: var(--clr-notification-title);
}

:root .Toastify__progress-bar--wrp {
  height: 2px;
  border-bottom-right-radius: 56px;
  border-bottom-left-radius: 56px;
  left: 5px;
  width: 97%;
}

:root .Toastify__progress-bar--bg {
  background: transparent;
  opacity: 0;
}

:root .Toastify__toast-theme--colored.Toastify__toast--info {
  background: var(--selected);
  color: var(--clr-info-toast-text);
}

:root .Toastify__progress-bar--info {
  background-color: var(--clr-general-primary) !important;
}

:root .Toastify__toast-theme--colored.Toastify__toast--success {
  background: var(--clr-green-100);
  color: var(--clr-notification-title);
}

:root .Toastify__progress-bar--success {
  background-color: var(--clr-green-400) !important;
}

:root .Toastify__toast-theme--colored.Toastify__toast--warning {
  background: var(--clr-yellow-200);
  color: var(--clr-notification-title);
}

:root .Toastify__progress-bar--warning {
  background-color: var(--clr-beige-200) !important;
}

:root .Toastify__toast-theme--colored.Toastify__toast--error {
  background: var(--clr-error-200);
  color: var(--clr-notification-title);
}

:root .Toastify__progress-bar--error {
  background-color: var(--clr-error-border) !important;
}

:root .refreshing-toast{
  width: 150px;
  align-items: center;
}

@keyframes refreshing-toast-spin {
  to {
    transform: rotate(360deg);
  }
}

.refreshing-toast-icon {
  display: inline-flex;
  font-size: 18px;
  animation: refreshing-toast-spin 900ms linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .refreshing-toast-icon {
    animation: none;
  }
}

.disabled-field{
  cursor: not-allowed;
  pointer-events: none;
}


input.ant-input-outlined:disabled:not(:placeholder-shown),
textarea.ant-input-outlined:disabled:not(:placeholder-shown),
.ant-input-affix-wrapper-disabled:has(input:not(:placeholder-shown)),
.ant-input-affix-wrapper-disabled:has(textarea:not(:placeholder-shown)),
.ant-input-number-disabled:has(input:not(:placeholder-shown)),
.ant-picker-disabled:has(input:not(:placeholder-shown)) {
  --field-disabled-bg: var(--clr-field-disabled);
}

/* antd freezes the hover background on disabled fields with a :hover:not([disabled])
   guard, which never matches a bare input or textarea because those carry the real
   attribute. Pin them explicitly so hovering a disabled field repaints nothing. */
input.ant-input-outlined:disabled:hover,
textarea.ant-input-outlined:disabled:hover {
  background-color: var(--field-disabled-bg, var(--clr-container-bg));
  border-color: var(--clr-border-grey-100);
}

.calendar-icon {
  font-size: 18px;
  color: var(--clr-general-description);
}

.sticky-note-icon{
  font-size: 18px;
  color: var(--clr-error-100);
}

.ant-picker-dropdown {
  left: 9px !important;
  .ant-picker-panel-container{
    padding: 10px;
    .ant-picker-panel-layout{
      border: 1px solid var(--clr-border-grey-100);
      border-radius: 5px;
      .ant-picker-date-panel {
        /* Leaves room for the 9px offset, padding and border on both sides in a narrow or zoomed pane. */
        max-width: min(280px, calc(var(--viewport-width) - 40px));
        .ant-picker-header{
          padding-inline: 60px;
          .ant-picker-header-view{
            line-height: 40px;
            button{
              font-size: 16px;
              font-weight: 500;
              line-height: 40px;
            }
          }
          
        }
        .ant-picker-body {
          padding: 10px;
          .ant-picker-content {
            thead{
              tr{
                th:first-child{
                  border-right: 1px solid var(--clr-border-grey-100);
                  padding-right: 6px;
                  &::after{
                    content: '#';
                    font-size: 13px;
                    color: var(--clr-title);
                  }
                
                }
              }
            }
            td.ant-picker-cell-week {
             border-right: 1px solid var(--clr-border-grey-100);
             cursor: not-allowed;
             pointer-events: none;
             .ant-picker-cell-inner{
              padding-right: 6px;
             }
            }
            td.ant-picker-cell-disabled{
              opacity: 0.6;
              &::before{
                background-color: transparent;
              }
            }
            td.ant-picker-cell.ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
              border: 0;
            }
            td.ant-picker-cell.ant-picker-cell-in-view.ant-picker-cell-today.ant-picker-cell-selected:not(.ant-picker-cell-disabled) .ant-picker-cell-inner {
              background: var(--clr-general-primary) ;
              color: var(--clr-white) ;
            }

            td.ant-picker-cell.ant-picker-cell-in-view.ant-picker-cell-today:not(.ant-picker-cell-selected) .ant-picker-cell-inner {
              background: var(--clr-general-primary) ;
              color: var(--clr-white) ;
            }
            &:has(.ant-picker-cell-selected){
              td.ant-picker-cell.ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner {
            background: var(--clr-general-hover);
            color: var(--clr-title);
              }
            }
          }
        }
      }
      .ant-picker-footer{
        .ant-picker-now{
          line-height: 40px;
          .ant-picker-now-btn{
            color: var(--clr-general-primary);
            font-size: 13px;
            font-weight: 500;
            &:hover{
              color: var(--clr-general-primary);
            }
          }
        }
      }
}
  }
}

.mail-list-container{
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
}

.recent-graph-container {
  height: 210px;
}
  .weekly-graph-container {
  height: 210px;
}
.ant-picker-dropdown{
  &.custom-time-picker{
    left: 0px !important;
  }
   .ant-picker-panel-container{
    box-shadow: 0px 0px 15px 0px rgba(var(--clr-general-dark-grey-rgb), 0.15);
   }
 .ant-picker-ranges{
  .ant-picker-ok{
    line-height: 34px;
    .ant-btn{
      border-radius: 5px;
      width: 44px;
      height: 28px;
      font-size: 13px;
    }
  }
  } 
.ant-picker-time-panel-column {
    &>li.ant-picker-time-panel-cell-selected {
      .ant-picker-time-panel-cell-inner{
        background-color: rgba(var(--clr-general-primary-rgb), 0.2);
      }
    }
  }

.ant-picker-time-panel .ant-picker-time-panel-column::after {
    height: 0;
  }

  /* Popup controls carry tabindex="-1", so this has to outrank the global reset that hides their ring. */
  [data-picker-cell]:focus-visible,
  [data-picker-time-cell]:focus-visible,
  .ant-picker-header button:focus-visible,
  .ant-picker-footer a:focus-visible,
  .ant-picker-footer button:focus-visible {
    outline: 2px solid var(--clr-general-primary);
    outline-offset: 1px;
  }

  /* Time columns scroll, which would clip a ring drawn outside the row. */
  [data-picker-time-cell]:focus-visible {
    outline-offset: -2px;
  }
}
.lineclamp-2{
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
.lineclamp-1{
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}
.white-space-wrap{
  white-space: break-spaces !important;
}
.white-space-nowrap{
  white-space: nowrap !important;
}
.ant-spin-fullscreen{
  z-index: 1200;
  .ant-spin-dot{
    font-size: 50px !important;
  }
}
.dashboard-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
}
.timesheet-table{
  border: 1px solid var(--clr-general-border-hover);
  border-radius: 5px;
  .ant-table{
  border-radius: 5px;
  .ant-table-container{
    border-radius: 5px;
    .ant-table-body{
      /* the only programmatic scroll here is the jump to today's column on
         load, which must land before the first paint rather than animate */
      scroll-behavior: auto;
      /* scrollbar styling is global (::-webkit-scrollbar); no standard
         scrollbar-color/width here, so the bar stays in overlay mode */
      border-radius: 5px;
      .ant-table-tbody{
        border-radius: 5px;
        .ant-table-row{
          &.total-row{
            td{
              padding-inline: 0;
              &:last-child{
                padding-right: 10px !important;
                p{
                  border-top-right-radius: 5px;
                  border-bottom-right-radius: 5px;
                }
              }
              &:first-child{
                padding-right: 0;
                padding-left: 10px !important;
                p{
                  color: var(--clr-title);
                  border-top-left-radius: 5px;
                  border-bottom-left-radius: 5px;
                  text-align: left;
                }
              }
              p{
                background-color: rgba(var(--clr-general-primary-rgb), 0.1);
                height: 30px;
                font-size: 11px;
                line-height: 17px;
                padding: 7px 10px;
                text-align: center;
                white-space: nowrap;
                color: var(--clr-general-description);
                &.current-day-total{
                  color: var(--clr-general-primary);
                }
              }
            }
            /* narrow panes squeeze the day columns, so the hour pills trade inline padding for digits */
            ${atPaneWidth(
              'max-width',
              449,
              css`
                td:not(:first-child) p {
                  padding-inline: 2px;
                }
              `,
            )}
          }
        td{
          &:has(.cell-content){
            vertical-align: bottom;
            /* row hover is off on this table, so only the day cell under the pointer lights up */
            &:hover{
              background: var(--ant-table-row-hover-bg);
            }
          }
          .matter-wrapper{
            display: flex;
            flex-direction: column;
            gap: 4px;
            h6{
              font-size: 13px;
              font-weight: 500;
              color: var(--clr-title);
              line-height: 18px;
            }
            p{
              font-size: 11px;
              font-weight: 400;
              color: var(--clr-title);
              line-height: 16px;
            }
  
          }
          .cell-content{
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 5px;
            .plus-icon-button{
              width: 20px;
              height: 20px;
              padding: 0;
              color: var(--clr-general-description);
              font-size: 18px;
              .ant-btn-icon{
                font-size: 18px;
              }
            }
            .hour-wrapper{
              .hours-clickable{
                height: 18px;
                padding: 0;
                line-height: 18px;
                font-size: 11px;
                font-weight: 400;
                &:not(:disabled):not(.ant-btn-disabled):hover {
                  background-color: transparent;
                }
              }
              p{
                line-height: 15px;
                font-size: 11px;
                color: var(--clr-general-primary);
                white-space: nowrap;
              }
            }
          }
        }
        }
        & > tr:last-child {
          td{
            border-bottom-color: transparent;
          }
        }
      }
    } 
  }
  }

   /* the pinned matter column carries the design's shadow itself, instead of antd's scroll-only inset on ::after */
   .ant-table-cell-fix-left-last{
     box-shadow: 0 4px 10px 0 var(--clr-shadow);
     &::after{
       display: none;
     }
   }
   .ant-table-ping-right{
      &:not(.ant-table-has-fix-right){
        .ant-table-container{
          &::after{
            box-shadow: none;
          }
        }
      }
   }
   .ant-table-thead >tr {
    & >th {
    padding-block: 10px;
    padding-inline: 4px;
    font-size: 13px;
    font-weight: 500;
    /* the fixed matter column keeps the body cells' inline padding so the
       header title stays aligned with the matter names underneath it */
    &:first-child{
      padding-inline: 10px;
    }
    .week-title-wrapper{
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding-block: 1.5px;
      border-radius: 5px;
      max-width: 52px;
      margin-inline: auto;
      span{
        font-size: 12px;
        color: var(--clr-title);
        line-height: 20px;
        font-weight: 400;
      }
      p{
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
         color: var(--clr-title);
         line-height: 21px;
      }
      &.current-day{
        background-color: var(--clr-general-primary);
        span {
          color: var(--clr-white);
        }
        p {
          color: var(--clr-white);
        }
      }
    }
      &:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before{
          display: none;
        }
        }
   }
  } 
.img-crop-modal{
  .ant-modal{
    margin: 0 auto;
    padding-bottom: 0;
    max-width: calc(var(--viewport-width) - 20px);
    .ant-modal-content{
      padding: 15px;
      .ant-modal-close{
        width: 20px;
        height: 20px;
        top: 15px;
        color: var(--clr-general-description);
        &:hover{
          background: transparent;
        }
      }
      .ant-modal-header{
        color: var(--clr-title);
        margin-bottom: 15px;
        background: var(--clr-container-bg);
        .ant-modal-title{
          color: var(--clr-title);
          line-height: 20px;
          font-size: 14px;
        }
      }
      .ant-modal-footer{
        margin-top: 20px;
        .ant-btn{
          height: 40px;
        }
      }
      .img-crop-control{
        width: 100%;
        gap: 8px;
        button{
          width: 24px;
          height: 24px;
          color: var(--clr-general-description);
          font-size: 20px;
          &:disabled{
            color: var(--clr-general-description);
            opacity: 0.4;
          }
          &:hover{
            color: var(--clr-general-primary);
          }
        }
        &.img-crop-control-zoom{
          margin-top: 15px; 
        }
        &.img-crop-control-rotation{
          margin-bottom: 0;
        
          margin-inline: 0;
          padding-top: 6px;
        }
      }
      .img-crop-container{
        border-radius: 5px;
      }
      .ant-modal-body{
        .ant-btn-default{
          position: absolute;
          border: 0;
          color: var(--clr-general-primary);
          font-size: 13px;
          font-weight: 500;
         right: 176px;
          bottom: 20px;
          &:not(:disabled):not(.ant-btn-disabled):hover{
            background-color: transparent;
            color: rgba(var(--clr-general-primary-rgb), 0.7);
          }
        }
      }
    }
  }
}
.photo-upload{
  .ant-upload{
    width: 100%;
  }
}
.ant-slider{
  margin: 0;
}

/* A plain custom property flips instantly, so the strip's gradient snapped to the
   hover colour while the field itself was still easing into it. Registering the
   property as a colour lets it transition, and the two halves move as one. */
@property --textarea-field-bg {
  syntax: '<color>';
  inherits: true;
  initial-value: transparent;
}

/* antd paints hoverBg/activeBg on the affix wrapper, not the textarea inside it,
   so the strip has to track the wrapper too. Watching the textarea left the strip
   on the resting colour whenever the pointer sat on the border or the counter. */
.textarea-field {
  /* keeps the counter's z-index inside the field, so a pinned save button scrolls over it */
  isolation: isolate;
  --textarea-field-bg: var(--clr-container-bg);
  /* the same duration antd puts on the field, so both ease together */
  transition: --textarea-field-bg var(--ant-motion-duration-mid);
  /* :not(:focus-within) keeps the two states mutually exclusive. Leaving them to
     the cascade let the hover rule win on specificity, so a field focused with the
     pointer still over it went white on top and hover-blue under the strip. */
  &:has(.ant-input-affix-wrapper:not(.ant-input-disabled):not(:focus-within):hover) {
    --textarea-field-bg: var(--clr-general-hover);
  }
  &:has(.ant-input-affix-wrapper:focus-within) {
    --textarea-field-bg: var(--clr-container-bg);
  }
  /* stop the strip short of the corner so it doesn't paint over the resize grip */
  &.resizable .textarea-field-footer:after {
    right: 15px;
  }
}
.textarea-field-footer {
  position: absolute;
  bottom: 0;
  padding: 10px;
  z-index: 9;
  border-radius: 0 0 5px 5px;
  pointer-events: none;
 .language-wrapper {
   .icon {
      font-size: 14px;
      color: var(--clr-general-description);
    }
  }
  &:after {
   content: '';
    position: absolute;
    bottom: 1px;
    left: 3px;
    right: 3px;
    height: 34px;
    background: linear-gradient(to bottom, transparent 0%, color-mix(in srgb, var(--textarea-field-bg) 100%, transparent 0%) 16%, var(--textarea-field-bg) 100%);
    pointer-events: none;
    z-index: -1;
}
  }

  .dashboard-menu {
    .ant-dropdown-menu-item {
      margin-bottom: 5px !important;
      min-width: 220px;
      .ant-dropdown-menu-title-content {
        display: flex;
        justify-content: space-between;
        .icon {
          font-size: 16px;
          color: var(--clr-general-description);
        }
      }
      .menu-item-btn {
        .icon {
          font-size: 18px;
          color: var(--clr-general-description);
        }
      }
      &:hover {
        background-color: var(--clr-general-hover) !important;
      }
      &:has(.active-item) {
        background-color: transparent !important;
        .menu-item-btn {
          span {
            color: var(--clr-general-primary);
          }
          .icon {
            color: var(--clr-general-primary);
          }
        }
      }
      &:last-child{
        margin-bottom: 0 !important;
      }
      &:nth-last-child(2) {
       
        position: relative;
        &::before {
          content: '';
          width: 100%;
          height: 1px;
          background-color: var(--clr-border-grey-100);
          position: absolute;
          top: 40px;
          left: 0;
        }
      }
    }
  } 

.ant-tabs-dropdown{
  .ant-tabs-dropdown-menu {
    padding: 10px;
    background-color: var(--clr-container-bg);
    border-radius: 5px;
    box-shadow: 0px 0px 30px 0px var(--clr-shadow);
    .ant-tabs-dropdown-menu-item{
      font-weight: 500;
      font-size: 13px;
      color: var(--clr-general-description);
      line-height: 17px;
      padding: 5px 10px;
      border-radius: 5px;
      margin-bottom: 5px;
      .custom-tab-label{
        display: flex;
        align-items: center;
      }
      &:last-child{
        margin-bottom: 0;
      }
      &:hover{
        background-color: var(--clr-general-hover);
      }
    }
  }
}

.chat-bubble {
  background-color: rgba(var(--clr-general-primary-rgb), 0.20);
  padding: 10px;
  -webkit-border-radius: 10px;
  -webkit-border-bottom-left-radius: 5px;
  -moz-border-radius: 10px;
  -moz-border-radius-bottomleft: 5px;
  border-radius: 10px;
  border-bottom-left-radius: 5px;
  display:inline-block;
}
.typing {
  align-items: center;
  display: flex;
}
.typing .dot {
  animation: mercuryTypingAnimation 1.8s infinite ease-in-out;
  background-color: var(--clr-subtitle); 
  border-radius: 50%;
  height: 6px;
  margin-right: 2px;
  vertical-align: middle;
  width: 6px;
  display: inline-block;
}
.typing .dot:nth-child(1) {
  animation-delay: 200ms;
}
.typing .dot:nth-child(2) {
  animation-delay: 300ms;
}
.typing .dot:nth-child(3) {
  animation-delay: 400ms;
}
.typing .dot:last-child {
  margin-right: 0;
}

@keyframes mercuryTypingAnimation {
  0% {
    transform: translateY(0px);
    background-color: rgba(var(--clr-subtitle-rgb), 0.7); 
  }
  28% {
    transform: translateY(-7px);
    background-color:rgba(var(--clr-subtitle-rgb), 0.4); 
  }
  44% {
    transform: translateY(0px);
    background-color: rgba(var(--clr-subtitle-rgb), 0.2); 
  }
}

`;

export default GlobalStyle;
